import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import api from "../lib/api";

const AuthContext = createContext(null);

const BOOTSTRAP_WATCHDOG_MS = 10000;
const SESSION_TIMEOUT_MS = 6000;
const API_TIMEOUT_MS = 7000;

function withTimeout(promise, ms, phase) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      const err = new Error(`timeout:${phase}`);
      err.phase = phase;
      reject(err);
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

function log(phase, extra) {
  if (typeof window === "undefined") return;
  // eslint-disable-next-line no-console
  console.log(`[boot] ${phase}`, extra || "");
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [bootRetry, setBootRetry] = useState(0);
  const bootstrapped = useRef(new Set());
  const inFlightBootstrap = useRef(null);

  const loadShadowUser = useCallback(async (nextSession) => {
    if (!nextSession?.access_token) {
      setUser(null);
      return null;
    }
    const sid = nextSession.user?.id;
    if (sid && !bootstrapped.current.has(sid)) {
      try {
        await withTimeout(api.post("/api/auth/bootstrap"), API_TIMEOUT_MS, "profile_loaded");
        bootstrapped.current.add(sid);
        log("profile_loaded");
      } catch (err) {
        log("profile_loaded_failed", err?.message);
        // Non-fatal — /me still returns data if row already exists.
      }
    }
    const res = await withTimeout(api.get("/api/auth/me"), API_TIMEOUT_MS, "role_resolved");
    log("role_resolved", { role: res.data?.role });
    // Auto-enable Platform Owner's personal operational context (idempotent).
    // Owner keeps `platform_owner` role; a dedicated membership (role=personal_owner)
    // is created so Owner can play Modo Caverna without consuming mentor capacity.
    if (res.data?.role === "platform_owner") {
      try {
        await api.post("/api/owner/personal-operation/enable");
      } catch (err) {
        log("owner_personal_op_enable_failed", err?.message);
      }
    }
    setUser(res.data);
    return res.data;
    // Deps intentionally empty: `api` and `supabase` are stable module
    // imports; `setUser`/`setBootError` setters are stable from React;
    // `bootstrapped`/`inFlightBootstrap` are refs (stable). Adding them
    // would re-create the callback every render for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runBootstrap = useCallback(async () => {
    log("bootstrap_started");
    setBootError(null);
    setLoading(true);
    try {
      log("auth_started");
      const { data } = await withTimeout(
        supabase.auth.getSession(),
        SESSION_TIMEOUT_MS,
        "auth_resolved"
      );
      log("auth_resolved", { hasSession: !!data.session });
      setSession(data.session);
      if (data.session) {
        await loadShadowUser(data.session);
      } else {
        // No Supabase session — try the Emergent Google cookie session.
        // The httpOnly `emergent_session` cookie is sent automatically by
        // axios (withCredentials). If it resolves, the user is logged in
        // via Google; otherwise we fall through to the public auth page.
        try {
          const res = await withTimeout(
            api.get("/api/auth/me"),
            API_TIMEOUT_MS,
            "google_cookie_resolved"
          );
          setUser(res.data || null);
          log("google_cookie_resolved", { role: res.data?.role });
        } catch (_) {
          setUser(null);
        }
      }
      log("app_ready");
    } catch (err) {
      log("bootstrap_error", { phase: err.phase, msg: err.message });
      if (err.phase === "auth_resolved") {
        setBootError({
          phase: err.phase,
          message: "Não conseguimos verificar sua sessão.",
        });
      } else if (err.phase === "role_resolved") {
        setBootError({
          phase: err.phase,
          message: "Não conseguimos carregar seu perfil.",
        });
      } else {
        setBootError({
          phase: err.phase || "unknown",
          message: "Não conseguimos carregar o Modo Caverna.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [loadShadowUser]);

  // Initial bootstrap + retry driver.
  useEffect(() => {
    let mounted = true;
    let watchdog = null;
    if (inFlightBootstrap.current) return () => {};
    inFlightBootstrap.current = (async () => {
      watchdog = setTimeout(() => {
        if (!mounted) return;
        log("bootstrap_watchdog_fired");
        setBootError((prev) =>
          prev || {
            phase: "watchdog",
            message: "Não conseguimos carregar o Modo Caverna.",
          }
        );
        setLoading(false);
      }, BOOTSTRAP_WATCHDOG_MS);
      try {
        await runBootstrap();
      } finally {
        if (watchdog) clearTimeout(watchdog);
        inFlightBootstrap.current = null;
      }
    })();
    return () => {
      mounted = false;
      if (watchdog) clearTimeout(watchdog);
    };
  }, [runBootstrap, bootRetry]);

  // Auth state listener — never triggers a full bootstrap, only refreshes the session/user.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      setSession(nextSession);
      if (event === "SIGNED_OUT" || !nextSession) {
        setUser(null);
        bootstrapped.current.clear();
        return;
      }
      try {
        await loadShadowUser(nextSession);
        // A late-resolving session clears a stale boot timeout error.
        setBootError(null);
      } catch (err) {
        log("onAuthStateChange_loadShadowUser_failed", err?.message);
      }
    });
    return () => sub?.subscription?.unsubscribe();
  }, [loadShadowUser]);

  const retryBootstrap = useCallback(() => {
    bootstrapped.current.clear();
    setBootRetry((r) => r + 1);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase(),
      password,
    });
    if (error) throw error;
    return data;
  }, []);

  const register = useCallback(
    async (name, email, password, phone) => {
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase(),
        password,
        options: { data: { name, phone } },
      });
      if (error) throw error;
      if (data.session) {
        try { await loadShadowUser(data.session); } catch (_) {}
        return data;
      }
      if (data.user && !data.user.email_confirmed_at && data.user.confirmation_sent_at) {
        return data;
      }
      try {
        const { data: signIn, error: signInErr } = await supabase.auth.signInWithPassword({
          email: email.toLowerCase(),
          password,
        });
        if (signInErr) throw signInErr;
        if (signIn.session) {
          try { await loadShadowUser(signIn.session); } catch (_) {}
        }
        return { ...data, session: signIn.session, user: signIn.user };
      } catch (fallbackErr) {
        return data;
      }
    },
    [loadShadowUser]
  );

  const logout = useCallback(async () => {
    bootstrapped.current.clear();
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (_) {}
    // Clear the Emergent Google session cookie too (idempotent — no-op
    // when the user signed in via Supabase and has no cookie set).
    try {
      await api.post("/api/auth/google/logout", {}, { withCredentials: true });
    } catch (_) {}
    setUser(null);
    setSession(null);
    setBootError(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  }, []);

  const switchDemoScenario = useCallback(async () => {
    // Dev Simulator has been fully removed in production. Role switching
    // is not allowed — clients must sign in with real Supabase credentials.
    throw new Error("Dev Simulator desativado.");
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await withTimeout(
        supabase.auth.getSession(),
        SESSION_TIMEOUT_MS,
        "auth_resolved"
      );
      if (data.session) {
        await loadShadowUser(data.session);
      } else {
        const res = await withTimeout(api.get("/api/auth/me"), API_TIMEOUT_MS, "google_cookie_resolved");
        setUser(res.data || null);
      }
    } catch (err) {
      log("refreshUser_failed", err?.message);
    }
  }, [loadShadowUser]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post("/api/auth/change-password", {
      current_password: currentPassword || undefined,
      new_password: newPassword,
    });
  }, []);

  // Refresh user data when tab returns to foreground (fixes stale "pending" access).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && (session?.access_token || user)) {
        refreshUser();
      }
    };
    const onOnline = () => {
      if (session?.access_token || user) refreshUser();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [session, user, refreshUser]);

  // Split the context value into two useMemo blocks so each has a
  // focused dependency list. The "actions" bundle only contains
  // useCallback-wrapped functions whose identity is stable, so it rarely
  // recomputes. The "state" bundle changes only when auth state really
  // changes. This keeps consumers that only read state from re-rendering
  // when actions change (and vice-versa).
  const authActions = useMemo(
    () => ({
      retryBootstrap,
      login,
      register,
      logout,
      requestPasswordReset,
      updatePassword,
      changePassword,
      switchDemoScenario,
      refreshUser,
    }),
    [
      changePassword,
      retryBootstrap,
      login,
      register,
      logout,
      requestPasswordReset,
      updatePassword,
      switchDemoScenario,
      refreshUser,
    ]
  );

  const value = useMemo(
    () => ({ session, user, loading, bootError, ...authActions }),
    [session, user, loading, bootError, authActions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
